import React from "react"

import { Button } from "@coval/ui/button"
import { Tooltip } from "@coval/ui/tooltip"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"

interface KnowledgeBaseDisableControlProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

/**
 * Knowledge-base grounding can no longer be turned on: it renders nothing for a
 * new or non-KB metric, and offers only a one-way disable for a metric that
 * already has it enabled from before the capability was deprecated.
 */
export const KnowledgeBaseDisableControl: React.FC<
  KnowledgeBaseDisableControlProps
> = ({ metricRecordData, onChange }) => {
  const labelId = React.useId()
  const knowledgeBaseEnabled =
    metricRecordData.metric_metadata?.use_knowledge_base || false

  if (!knowledgeBaseEnabled) {
    return null
  }

  const handleDisable = () => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        use_knowledge_base: false,
      },
    })
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className={LABEL_ROW_STYLES}>
        <span id={labelId} className={LABEL_STYLES}>
          Knowledge Base
        </span>
        <Tooltip content="This metric was created with knowledge-base grounding. It can be disabled, but knowledge-base grounding can't be re-enabled.">
          <button type="button" className="inline-flex" aria-label="Info">
            <Info className={INFO_ICON_STYLES} />
          </button>
        </Tooltip>
      </div>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex items-center gap-3 h-control"
      >
        <span className="text-sm text-coval-text">Enabled</span>
        <Button
          variant="secondary"
          onClick={handleDisable}
          aria-label="Disable knowledge base"
        >
          Disable
        </Button>
      </div>
    </div>
  )
}
